/* =========================================================================
   Ilene Joy — unified interaction lib
   =========================================================================
   Shared runtime consumed by every page via layout/theme.liquid. Responsible
   for: the top progress bar, opening/closing the cart drawer, rendering the
   drawer contents from /cart.js, handling drawer remove clicks, and
   intercepting the header cart icon so it opens the drawer instead of
   navigating to /cart on every click.

   Global API exposed on window.IJ:
     IJ.openDrawer()       — open + refresh
     IJ.closeDrawer()
     IJ.refreshDrawer()    — re-fetch /cart.js + re-render
     IJ.progress.start()   — show top bar
     IJ.progress.stop()    — hide top bar

   Safety: ALL DOM content insertion goes through document.createElement +
   textContent — never innerHTML with untrusted data. Shopify cart data
   (product titles, prices) is technically trusted but we still avoid
   innerHTML to stay XSS-safe against future content.
   ========================================================================= */

(function () {
  'use strict';

  if (window.IJ && window.IJ.__ready) return;

  var $ = function (sel, root) { return (root || document).querySelector(sel); };
  var $$ = function (sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); };
  /* Money rule shared with the theme (money_without_trailing_zeros) and the builder:
     cents only when non-zero, en-US grouping, never round (2026-09-05). */
  var MONEY = function (cents) { var n = Math.round(Number(cents || 0)); var d = n % 100 === 0 ? 0 : 2; return '$' + (n / 100).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d }); };

  // Remove every child of an element without using innerHTML.
  function clearChildren(el) {
    while (el && el.firstChild) el.removeChild(el.firstChild);
  }

  // ========================================================================
  // Progress bar
  // ========================================================================
  var progressEl = null;
  var progressDepth = 0;

  function ensureProgress() {
    if (progressEl) return progressEl;
    progressEl = document.createElement('div');
    progressEl.className = 'ij-progress';
    progressEl.setAttribute('aria-hidden', 'true');
    document.body.appendChild(progressEl);
    return progressEl;
  }
  function progressStart() {
    ensureProgress();
    progressDepth++;
    progressEl.classList.add('is-active');
  }
  function progressStop() {
    progressDepth = Math.max(0, progressDepth - 1);
    if (progressDepth === 0 && progressEl) progressEl.classList.remove('is-active');
  }

  // ========================================================================
  // Snapshot capture — HYBRID: native canvas renderer (from React bundle
  // via window.__IJ_RENDER_SNAPSHOT__) preferred. Falls back to html2canvas
  // only if the bundle hasn't loaded the global by the time the user hits
  // add-to-cart (rare race: customer clicks before our 538KB bundle parses).
  //
  // WHY NATIVE
  // ----------
  // html2canvas re-implements CSS layout, gets it wrong with `aspect-ratio`
  // and CSS transforms, and produced charms-out-of-position renders that
  // misled the bench team about customer pieces. Native walks the LAID-OUT
  // DOM and paints what's already there. Pixel-perfect.
  //
  // CHROME FILTERING
  // ----------------
  // The flatlay frame contains the chain, MM/lariat overlays, charm
  // markers — AND the SAVE/SHARE action buttons, the PLATE caption bar,
  // the rotate handle that appears on charm-tap, the drag-hint pill, etc.
  // We strip all of those via ignoreSelectors so the keepsake is the
  // composition only.
  // ========================================================================
  var SNAPSHOT_TIMEOUT_MS = 8000;
  var SNAPSHOT_IGNORE_SELECTORS = [
    '.ij__preview-cap',
    '.ij__actions',
    '.ij__action',
    '.ij__frame-pulse',
    '.ij__drag-hint',
    '.ij__charm-rotate-handle',
    '.ij__charm-controls',
    '.ij__model-debug-toggle',
    '.ij__model-overlay-box',
    '.ij__hint-pill',
  ];

  /* Lazy-load html2canvas only if we're about to use the fallback path.
     Keeps the page weight down for the 99% case where the bundle loaded
     and we use the native renderer. */
  var html2canvasReady = null;
  function ensureHtml2Canvas() {
    if (window.html2canvas) return Promise.resolve(window.html2canvas);
    if (html2canvasReady) return html2canvasReady;
    html2canvasReady = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js';
      s.onload = function () { resolve(window.html2canvas); };
      s.onerror = reject;
      document.head.appendChild(s);
    });
    return html2canvasReady;
  }

  function captureViaHtml2Canvas(target) {
    var rect = target.getBoundingClientRect();
    var padX = 48;
    var padTop = 48;
    var padBottom = 96;
    return ensureHtml2Canvas().then(function (h2c) {
      return h2c(target, {
        backgroundColor: '#ffffff',
        useCORS: true,
        allowTaint: false,
        scale: 3,
        logging: false,
        width: rect.width + padX * 2,
        height: rect.height + padTop + padBottom,
        x: -padX,
        y: -padTop,
        ignoreElements: function (node) {
          for (var i = 0; i < SNAPSHOT_IGNORE_SELECTORS.length; i++) {
            try { if (node.matches(SNAPSHOT_IGNORE_SELECTORS[i])) return true; }
            catch (_) { /* invalid selector */ }
          }
          return false;
        },
        onclone: function (_doc, cloned) {
          cloned.style.overflow = 'visible';
          cloned.style.backgroundColor = '#ffffff';
          var pulse = cloned.querySelector('.ij__frame-pulse');
          if (pulse) pulse.style.display = 'none';
        },
      });
    }).then(function (canvas) {
      return new Promise(function (resolve) {
        canvas.toBlob(function (blob) { resolve(blob); }, 'image/jpeg', 0.95);
      });
    });
  }

  /* Capture the flatlay as a JPEG blob. Returns null on any failure — the
     caller falls back to a no-snapshot cart submission. */
  function captureDesignSnapshot() {
    var target = document.querySelector('.ij__preview-frame--flatlay');
    if (!target) return Promise.resolve(null);

    /* HYBRID PATH 1: native canvas renderer exposed by the React bundle.
       Called the same way as html2canvas but renders pixel-perfectly to
       what's on screen. Uses CORS-refetched images so no canvas tainting. */
    var nativeFn = window.__IJ_RENDER_SNAPSHOT__;
    if (typeof nativeFn === 'function') {
      return nativeFn(target, {
        ignoreSelectors: SNAPSHOT_IGNORE_SELECTORS,
        backgroundColor: '#fbfaf7', /* v0.9.169: light plate tone (was cream) */
        quality: 0.95,
      })
        .then(function (blob) { return blob; })
        .catch(function (err) {
          if (window.console) console.warn('[ij] native snapshot failed, falling back to html2canvas', err);
          return captureViaHtml2Canvas(target).catch(function (err2) {
            if (window.console) console.warn('[ij] html2canvas fallback also failed', err2);
            return null;
          });
        });
    }

    /* HYBRID PATH 2: bundle hasn't loaded yet (or this build is older
       than the one that exposed the global). Use html2canvas. */
    return captureViaHtml2Canvas(target).catch(function (err) {
      if (window.console) console.warn('[ij] snapshot capture failed', err);
      return null;
    });
  }

  /* Wrap captureDesignSnapshot in a hard timeout. html2canvas sometimes
     hangs indefinitely (font-load race, CORS-tainted image, SVG recursion).
     After SNAPSHOT_TIMEOUT_MS we resolve to null so the cart submit can
     fall through without the customer staring at a frozen "Adding to
     cart…" state for 30+ seconds. */
  function captureDesignSnapshotWithTimeout() {
    return new Promise(function (resolve) {
      var settled = false;
      var timer = setTimeout(function () {
        if (settled) return;
        settled = true;
        if (window.console) console.warn('[ij] snapshot capture timed out at ' + SNAPSHOT_TIMEOUT_MS + 'ms; submitting without snapshot');
        resolve({ blob: null, status: 'timeout' });
      }, SNAPSHOT_TIMEOUT_MS);
      captureDesignSnapshot().then(function (blob) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve({ blob: blob, status: blob ? 'ok' : 'capture_failed' });
      });
    });
  }

  // ========================================================================
  // Fetch interceptor — progress bar, snapshot attach, event emit
  // ========================================================================
  var CART_URL_RE = /\/cart\/(add|change|update|clear)\.js/i;
  var ADD_URL_RE = /\/cart\/add\.js/i;
  var origFetch = window.fetch;

  /* Transform a JSON /cart/add.js body into a multipart FormData with the
     snapshot attached to the parent (chain) line item. Shopify's cart
     endpoint accepts the items[N][field] syntax in multipart, stores the
     file blob in Shopify Files CDN, and exposes the URL as a line-item
     property on the order + in /cart.js. Returns null if snapshot missing
     or body shape unexpected, signalling the caller to fall back. */
  function buildAddMultipart(jsonBody, snapshotResult) {
    if (!jsonBody || !Array.isArray(jsonBody.items) || jsonBody.items.length === 0) return null;
    /* Inject _bundle_id here because the charm-builder.liquid injector only
       sees JSON bodies — the moment we switch to FormData below, that
       inline injector no-ops. Stamping every item in the same add-call
       with a shared id keeps the cart-drawer consolidation working. */
    var bundleId = 'bundle-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
    var fd = new FormData();
    /* Find the parent (chain) line up front — we need it for snapshot
       attachment AND for the _snapshot_status property. */
    var parentIdx = jsonBody.items.findIndex(function (i) {
      return i.properties && i.properties._arrangement_json;
    });
    if (parentIdx < 0) parentIdx = 0;
    jsonBody.items.forEach(function (item, i) {
      var props = Object.assign({}, item.properties || {});
      if (!props._bundle_id) props._bundle_id = bundleId;
      /* Stamp every line item with the snapshot status. Fulfillment queries
         by _bundle_id → if status is 'ok' they pull the _design file;
         otherwise they know the customer's design wasn't captured and they
         should cross-reference the _arrangement_json to reconstruct the
         piece, or reach out to the customer for a re-submission. */
      if (i === parentIdx && snapshotResult) {
        props._snapshot_status = snapshotResult.status;
      }
      fd.append('items[' + i + '][id]', String(item.id));
      fd.append('items[' + i + '][quantity]', String(item.quantity || 1));
      Object.keys(props).forEach(function (k) {
        fd.append('items[' + i + '][properties][' + k + ']', String(props[k]));
      });
    });
    if (snapshotResult && snapshotResult.blob) {
      /* Attach to the parent (chain) line item — the one carrying
         _arrangement_json. Underscore-prefixed property so it's HIDDEN from
         the customer's order confirmation email (Shopify convention) while
         staying visible to the atelier team on the order admin. The cart
         template still reads this via item.properties['_design'] to render
         the thumbnail. */
      fd.append('items[' + parentIdx + '][properties][_design]', snapshotResult.blob, 'design-preview.jpg');
    }
    return fd;
  }

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var isCartMut = CART_URL_RE.test(url);
    var isAdd = ADD_URL_RE.test(url);

    if (isAdd && init && typeof init.body === 'string') {
      /* Snapshot path: capture the flatlay then POST multipart with file.
         If snapshot fails we fall through to the vanilla JSON path so the
         purchase never gets blocked on image-capture infrastructure. */
      try {
        var jsonBody = JSON.parse(init.body);
        progressStart();
        return captureDesignSnapshotWithTimeout().then(function (snapshotResult) {
          var fd = buildAddMultipart(jsonBody, snapshotResult);
          if (!fd) return origFetch(input, init); // shape unexpected — fall through
          /* Don't copy Content-Type from init — browser sets it with the
             right multipart boundary when body is FormData. */
          var newInit = { method: 'POST', body: fd, credentials: init.credentials || 'same-origin' };
          return origFetch(url, newInit);
        }).then(function (res) {
          try {
            res.clone().json().then(function (data) {
              window.dispatchEvent(new CustomEvent('ij:cart-mutated', { detail: { url: url, data: data } }));
              window.dispatchEvent(new CustomEvent('ij:cart-added', { detail: { data: data } }));
            }).catch(function () {});
          } catch (_) {}
          return res;
        }).finally(function () { progressStop(); });
      } catch (_) {
        /* Body wasn't valid JSON — let it through unchanged. */
      }
    }

    if (isCartMut) progressStart();
    var p = origFetch.apply(this, arguments);
    if (isCartMut) {
      p.then(function (res) {
        try {
          var clone = res.clone();
          clone.json().then(function (data) {
            window.dispatchEvent(new CustomEvent('ij:cart-mutated', { detail: { url: url, data: data } }));
            if (isAdd) {
              window.dispatchEvent(new CustomEvent('ij:cart-added', { detail: { data: data } }));
            }
          }).catch(function () {});
        } catch (_) {}
      }).finally(function () { progressStop(); });
    }
    return p;
  };

  // ========================================================================
  // Ripple tap feedback
  // ========================================================================
  function addRipple(el) {
    if (!el || el.__ijTap) return;
    el.__ijTap = true;
    el.classList.add('ij-tap');
    el.addEventListener('click', function () {
      el.classList.remove('is-pressed');
      void el.offsetWidth;
      el.classList.add('is-pressed');
      setTimeout(function () { el.classList.remove('is-pressed'); }, 400);
    });
  }

  function wireCartButtons() {
    $$('.nav-cart a, [data-custom-cart-remove], .quantity-minus, .quantity-plus, .cart__checkout, [name="checkout"], .ij-drawer-checkout, .ij-drawer-bundle-remove').forEach(addRipple);
  }

  // ========================================================================
  // Cart drawer
  // ========================================================================
  var drawerEl = null;
  var scrimEl = null;
  var drawerBodyEl = null;
  var drawerFootEl = null;
  var drawerCountEl = null;

  function mountDrawer() {
    if (drawerEl) return;
    drawerEl = $('.ij-cart-drawer');
    scrimEl = $('.ij-drawer-scrim');
    if (!drawerEl || !scrimEl) return;
    drawerBodyEl = $('.ij-drawer-body', drawerEl);
    drawerFootEl = $('.ij-drawer-foot', drawerEl);
    drawerCountEl = $('.ij-drawer-count', drawerEl);

    scrimEl.addEventListener('click', closeDrawer);
    $('.ij-drawer-close', drawerEl).addEventListener('click', closeDrawer);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawerEl.classList.contains('is-open')) closeDrawer();
    });
  }

  function renderLoading() {
    if (!drawerBodyEl) return;
    clearChildren(drawerBodyEl);
    var spinner = document.createElement('div');
    spinner.className = 'ij-drawer-empty';
    var p = document.createElement('p');
    p.textContent = 'Loading…';
    p.style.color = 'var(--ij-ink-3)';
    spinner.appendChild(p);
    drawerBodyEl.appendChild(spinner);
    if (drawerFootEl) drawerFootEl.style.display = 'none';
  }

  function openDrawer() {
    mountDrawer();
    if (!drawerEl) return;
    // Clear stale content from any previous render BEFORE showing the panel,
    // then kick off the refresh. Prevents the "old cart total flashes for 1s"
    // race where the panel animates in showing the previous cart state.
    renderLoading();
    drawerEl.classList.add('is-open');
    scrimEl.classList.add('is-open');
    document.documentElement.classList.add('ij-drawer-open');
    document.body.classList.add('ij-drawer-open');
    refreshDrawer();
  }
  function closeDrawer() {
    if (!drawerEl) return;
    drawerEl.classList.remove('is-open');
    scrimEl.classList.remove('is-open');
    document.documentElement.classList.remove('ij-drawer-open');
    document.body.classList.remove('ij-drawer-open');
  }

  function renderEmpty() {
    clearChildren(drawerBodyEl);
    var wrap = document.createElement('div');
    wrap.className = 'ij-drawer-empty';
    var p = document.createElement('p');
    p.textContent = 'Your cart is empty.';
    var a = document.createElement('a');
    a.href = '/products/box';
    a.textContent = 'Design a necklace';
    wrap.appendChild(p);
    wrap.appendChild(a);
    drawerBodyEl.appendChild(wrap);
    if (drawerFootEl) drawerFootEl.style.display = 'none';
    if (drawerCountEl) drawerCountEl.textContent = '';
  }

  /**
   * Count the customer-facing "designs" in a cart.
   *
   * A bundle (multi-line-item design like Chain + MM + Lariat + 7 charms)
   * counts as ONE design, no matter how many line items Shopify breaks
   * it into internally. Loose items (legacy carts, non-bundle products)
   * count as themselves.
   *
   * Why: the customer thinks of their custom piece as ONE thing. Showing
   * "6" on the cart icon when they just designed one piece is alarming
   * + misleading. This count matches the drawer's bundle UI.
   *
   * Falls back to cart.item_count when items array is missing.
   */
  function computeDesignCount(cart) {
    if (!cart || !Array.isArray(cart.items)) {
      return cart && typeof cart.item_count === 'number' ? cart.item_count : 0;
    }
    var bundleIds = {};
    var loners = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      var bid = item.properties && item.properties._bundle_id;
      if (bid) bundleIds[bid] = true;
      else loners++;
    }
    return Object.keys(bundleIds).length + loners;
  }

  /**
   * Update every cart-count display in the page.
   *
   * The header cart icon uses `[data-cart-render="item_count"]` (legacy
   * CartJS hook in vendor.js, normally only updated when a request goes
   * through CartJS's queue). Our drawer uses `origFetch` directly to
   * bypass CartJS — so we update both spots manually.
   *
   * Also updates `data-cart-render="design_count"` if any future template
   * wants the design-aware count specifically.
   */
  function updateCartCounts(cart) {
    var designs = computeDesignCount(cart);
    /* External header icon — was showing raw item_count (e.g. "6"
       for one bundle). Now matches what the drawer shows: design count. */
    var headerCountEls = document.querySelectorAll('[data-cart-render="item_count"]');
    for (var i = 0; i < headerCountEls.length; i++) {
      headerCountEls[i].textContent = String(designs);
      /* Hide the badge entirely when cart is empty — nicer than
         showing a "0" that catches the eye. */
      headerCountEls[i].style.visibility = designs === 0 ? 'hidden' : '';
    }
    /* Future-proof: any new templates can use design_count semantically. */
    var designCountEls = document.querySelectorAll('[data-cart-render="design_count"]');
    for (var j = 0; j < designCountEls.length; j++) {
      designCountEls[j].textContent = String(designs);
    }
    /* Drawer header — same design count, with units. */
    if (drawerCountEl) {
      drawerCountEl.textContent = designs + (designs === 1 ? ' item' : ' items');
    }
  }

  function renderDrawer(cart) {
    if (!drawerBodyEl) return;
    clearChildren(drawerBodyEl);

    /* Update icon counts FIRST so the header is in sync even if the
       drawer body render below errors out. */
    updateCartCounts(cart);

    if (!cart.items || !cart.items.length) { renderEmpty(); return; }

    var groups = {};
    var loners = [];
    cart.items.forEach(function (item) {
      var bid = item.properties && item.properties._bundle_id;
      if (bid) { (groups[bid] = groups[bid] || []).push(item); }
      else { loners.push(item); }
    });

    Object.keys(groups).forEach(function (bid) {
      var items = groups[bid];
      var parent = items.find(function (i) { return i.properties && i.properties._arrangement_json; }) || items[0];
      var children = items.filter(function (i) { return i !== parent; });
      drawerBodyEl.appendChild(renderBundle(parent, children, items));
    });
    loners.forEach(function (item) {
      drawerBodyEl.appendChild(renderBundle(item, [], [item]));
    });

    if (drawerFootEl) {
      drawerFootEl.style.display = '';
      var subtotalEl = $('.ij-drawer-subtotal .amount', drawerFootEl);
      if (subtotalEl) subtotalEl.textContent = MONEY(cart.total_price);
    }
  }

  function renderBundle(parent, children, allItems) {
    var row = document.createElement('div');
    row.className = 'ij-drawer-bundle';
    row.dataset.parentKey = parent.key;

    var img = document.createElement('img');
    img.className = 'ij-drawer-thumb';
    /* Prefer the customer's uploaded design snapshot over the product's
       default featured image — the whole point of this cart is the piece
       they designed. The snapshot is attached as a line-item file property
       named "Design" by our /cart/add.js interceptor; Shopify replaces the
       property value with the CDN URL after upload. Falls back gracefully
       if the snapshot wasn't captured (old sessions, capture errors). */
    // v0.5.7: key is `_design` now (underscore hides from order confirmation
    // emails). Fall back to legacy `Design` for carts that persisted before
    // the rename — customers with in-flight carts keep working.
    var designUrl = parent.properties && (parent.properties['_design'] || parent.properties['Design']);
    img.src = designUrl || parent.image || '';
    img.alt = parent.title || '';
    img.loading = 'lazy';
    row.appendChild(img);

    var info = document.createElement('div');
    info.className = 'ij-drawer-bundle-info';

    var title = document.createElement('h3');
    title.className = 'ij-drawer-bundle-title';
    title.textContent = parent.product_title || parent.title;
    info.appendChild(title);

    if (parent.variant_title && parent.variant_title !== 'Default Title') {
      var variant = document.createElement('div');
      variant.className = 'ij-drawer-bundle-variant';
      variant.textContent = parent.variant_title;
      info.appendChild(variant);
    }

    /* 'Edit design' (2026-09-05): the builder stamps `_edit_url` (path + full design
       query) on the chain parent at add time. Only builder pieces carry it — ordinary
       products get no link. Guard: same-origin /products/ path only, so a crafted
       property can never become javascript:; href is set as a property, never via
       innerHTML. */
    var editUrl = parent.properties && parent.properties._edit_url;
    if (typeof editUrl === 'string' && /^\/products\/[a-z0-9-]+\?[^\s"'<>]{1,8000}$/i.test(editUrl)) {
      var edit = document.createElement('a');
      edit.className = 'ij-drawer-bundle-edit';
      edit.href = editUrl;
      edit.textContent = 'Edit design';
      info.appendChild(edit);
    }

    if (children.length) {
      var kids = document.createElement('div');
      kids.className = 'ij-drawer-bundle-children';
      children.forEach(function (c) {
        var line = document.createElement('div');
        var label = document.createElement('span');
        label.textContent = '+ ' + (c.product_title || c.title || '');
        var price = document.createElement('span');
        price.textContent = MONEY(c.final_line_price);
        line.appendChild(label);
        line.appendChild(price);
        kids.appendChild(line);
      });
      info.appendChild(kids);
    }

    var totalCents = allItems.reduce(function (acc, i) { return acc + i.final_line_price; }, 0);
    var totalRow = document.createElement('div');
    totalRow.className = 'ij-drawer-bundle-total';
    var totalLabel = document.createElement('span');
    totalLabel.textContent = MONEY(totalCents);
    var removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'ij-drawer-bundle-remove';
    removeBtn.textContent = 'Remove';
    removeBtn.addEventListener('click', function () {
      removeBundle(removeBtn, allItems);
    });
    addRipple(removeBtn);
    totalRow.appendChild(totalLabel);
    totalRow.appendChild(removeBtn);
    info.appendChild(totalRow);

    row.appendChild(info);
    return row;
  }

  /**
   * Optimistic bundle remove.
   *
   * The OLD flow waited for /cart/update.js to round-trip (which under
   * Shopify cart-app cascades can take 30-60s for an 11-item bundle)
   * and only then updated the drawer. Customers stared at a "Remove"
   * button for a minute. Worse, refreshDrawer was called twice — once
   * via the ij:cart-mutated event listener, once via the explicit .then.
   *
   * NEW flow: optimistic.
   *   1. Animate the bundle row out IMMEDIATELY (fade + slide).
   *   2. Update the drawer subtotal client-side using the items we know
   *      we're removing (so the total flashes to the correct new value).
   *   3. Fire the /cart/update.js call in the background.
   *   4. On success: do nothing — the optimistic state IS correct, and the
   *      ij:cart-mutated event the fetch interceptor dispatches will
   *      trigger a refreshDrawer that reconciles any other drift.
   *   5. On error: rollback — re-insert the bundle row + restore subtotal +
   *      toast message. (User decides UX of the rollback toast — see TODO.)
   *
   * This makes the perceived removal latency ~0ms regardless of how
   * slow the cart endpoint is.
   */
  function removeBundle(btn, items) {
    if (btn.classList.contains('ij-btn-loading')) return;
    btn.classList.add('ij-btn-loading');

    /* 1. OPTIMISTIC DOM REMOVAL — capture the bundle row + the next
          sibling so we know where to restore on rollback. */
    var bundleRow = btn.closest('.ij-drawer-bundle');
    var nextSibling = bundleRow ? bundleRow.nextSibling : null;
    var parentEl = bundleRow ? bundleRow.parentNode : null;
    if (bundleRow) {
      bundleRow.style.transition = 'opacity 220ms ease, transform 220ms ease, max-height 280ms ease, margin 280ms ease, padding 280ms ease';
      bundleRow.style.maxHeight = bundleRow.offsetHeight + 'px'; // pin for transition
      requestAnimationFrame(function () {
        bundleRow.style.opacity = '0';
        bundleRow.style.transform = 'translateX(8px)';
        bundleRow.style.maxHeight = '0px';
        bundleRow.style.marginTop = '0';
        bundleRow.style.marginBottom = '0';
        bundleRow.style.paddingTop = '0';
        bundleRow.style.paddingBottom = '0';
      });
      setTimeout(function () {
        if (bundleRow.parentNode) bundleRow.parentNode.removeChild(bundleRow);
      }, 300);
    }

    /* 2. OPTIMISTIC SUBTOTAL — subtract the bundle's total from the
          drawer subtotal so the total reflects the new state instantly. */
    var removedTotal = items.reduce(function (acc, i) { return acc + (i.final_line_price || 0); }, 0);
    var subtotalEl = drawerFootEl ? $('.ij-drawer-subtotal .amount', drawerFootEl) : null;
    var prevSubtotalText = subtotalEl ? subtotalEl.textContent : null;
    if (subtotalEl) {
      var mm = (prevSubtotalText || '').match(/\$([\d,]+)(?:\.(\d{2}))?/);
      var currentCents = mm ? parseInt(mm[1].replace(/,/g, ''), 10) * 100 + (mm[2] ? parseInt(mm[2], 10) : 0) : NaN;
      if (!isNaN(currentCents)) {
        subtotalEl.textContent = MONEY(Math.max(0, currentCents - removedTotal));
      }
    }
    /* Optimistic item count: a bundle = 1 design. Decrement BOTH the
       drawer header AND the external cart-icon badge. We don't know the
       new total raw cart count without a /cart.js fetch, but we know
       this remove subtracts exactly ONE design. */
    var prevCountText = drawerCountEl ? drawerCountEl.textContent : null;
    var prevHeaderCounts = [];
    var headerCountEls = document.querySelectorAll('[data-cart-render="item_count"]');
    for (var hi = 0; hi < headerCountEls.length; hi++) {
      prevHeaderCounts.push({ el: headerCountEls[hi], text: headerCountEls[hi].textContent, vis: headerCountEls[hi].style.visibility });
    }
    if (drawerCountEl && prevCountText) {
      var prevCount = parseInt(prevCountText, 10);
      if (!isNaN(prevCount)) {
        var newCount = Math.max(0, prevCount - 1);
        drawerCountEl.textContent = newCount + (newCount === 1 ? ' item' : ' items');
      }
    }
    for (var hi2 = 0; hi2 < headerCountEls.length; hi2++) {
      var prev = parseInt(headerCountEls[hi2].textContent, 10);
      if (!isNaN(prev)) {
        var nextN = Math.max(0, prev - 1);
        headerCountEls[hi2].textContent = String(nextN);
        headerCountEls[hi2].style.visibility = nextN === 0 ? 'hidden' : '';
      }
    }

    /* 3. BACKGROUND API CALL — fire-and-forget from the user's
          perspective. The fetch interceptor still fires ij:cart-mutated
          on success, which triggers a refreshDrawer in the listener
          below (line ~575). That refresh reconciles any drift between
          our optimistic state and Shopify's authoritative state.
          We use `origFetch` directly to bypass the wrapper's progress
          bar — we already removed the row, no need to flash a loading
          state. */
    var updates = {};
    items.forEach(function (i) { updates[i.key] = 0; });
    origFetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates }),
    }).then(function (res) {
      if (!res.ok) throw new Error('cart/update.js status ' + res.status);
      /* Manually fire ij:cart-mutated so the listener refreshes the
         drawer to reconcile (origFetch bypasses the interceptor's
         event dispatch). The refresh re-fetches /cart.js and calls
         renderDrawer → updateCartCounts → header icon syncs to truth. */
      window.dispatchEvent(new CustomEvent('ij:cart-mutated', { detail: { url: '/cart/update.js' } }));
    }).catch(function (err) {
      /* 4. ROLLBACK — the API call failed. We need to put things back. */
      console.error('[ij] bundle remove failed', err);
      handleRemoveBundleFailure(bundleRow, parentEl, nextSibling, prevSubtotalText, prevCountText, subtotalEl, prevHeaderCounts);
    });
  }

  /**
   * Restore the drawer state when a bundle remove API call fails.
   *
   * Parameters:
   *   bundleRow:        the DOM node we removed (already detached)
   *   parentEl:         the drawer container we removed it from
   *   nextSibling:      the node it was sitting before (null = was at end)
   *   prevSubtotalText: the cart subtotal before the optimistic update
   *   prevCountText:    the item count text before the optimistic update
   *   subtotalEl:       the subtotal DOM node to restore (may be null)
   *
   * TODO[user]: Decide the rollback UX (5-10 lines). This is a meaningful
   * UX choice — what should the customer see when a remove fails?
   *
   * Options to consider:
   *   A. Silent restore — re-insert the row, restore totals, show nothing.
   *      User sees the bundle "come back" with no explanation. They're
   *      confused but the cart state is correct.
   *   B. Toast notification — "Couldn't remove right now. Try again?"
   *      Explicit but adds a UI element that doesn't exist anywhere else
   *      in the drawer. Need a toast component.
   *   C. Inline error in the bundle row — small red text under the
   *      Remove button: "Couldn't remove. Tap to retry."
   *      Fits the existing UI; clear next action.
   *   D. Auto-retry with backoff — try again after 1s, 3s, 7s before
   *      surfacing any error. Hides transient network blips entirely.
   *
   * Pick A/B/C/D (or hybrid) and implement below the marker.
   */
  function handleRemoveBundleFailure(bundleRow, parentEl, nextSibling, prevSubtotalText, prevCountText, subtotalEl, prevHeaderCounts) {
    /* --- Minimum viable rollback (always do this) --- */
    if (bundleRow && parentEl) {
      bundleRow.style.transition = '';
      bundleRow.style.opacity = '';
      bundleRow.style.transform = '';
      bundleRow.style.maxHeight = '';
      bundleRow.style.marginTop = '';
      bundleRow.style.marginBottom = '';
      bundleRow.style.paddingTop = '';
      bundleRow.style.paddingBottom = '';
      var btn = bundleRow.querySelector('.ij-drawer-bundle-remove');
      if (btn) btn.classList.remove('ij-btn-loading');
      if (nextSibling && nextSibling.parentNode === parentEl) {
        parentEl.insertBefore(bundleRow, nextSibling);
      } else {
        parentEl.appendChild(bundleRow);
      }
    }
    if (subtotalEl && prevSubtotalText !== null) subtotalEl.textContent = prevSubtotalText;
    if (drawerCountEl && prevCountText !== null) drawerCountEl.textContent = prevCountText;
    /* Restore header cart-icon badge values (and visibility) we
       optimistically decremented before the failed API call. */
    if (Array.isArray(prevHeaderCounts)) {
      for (var i = 0; i < prevHeaderCounts.length; i++) {
        prevHeaderCounts[i].el.textContent = prevHeaderCounts[i].text;
        prevHeaderCounts[i].el.style.visibility = prevHeaderCounts[i].vis || '';
      }
    }

    /* --- TODO[user]: choose A / B / C / D and implement here --- */
  }

  /**
   * Sync the cart-icon badge on initial page load.
   *
   * Without this, the Liquid-rendered initial value (cart.item_count =
   * raw line items, e.g. "6" for one bundle) shows until the customer
   * opens the drawer and triggers our renderDrawer → updateCartCounts.
   *
   * Fetch /cart.js once at startup, compute the design count, push to
   * the badge. Silent failure — if cart.js is rate limited or down, we
   * just leave the Liquid initial value (worst case: matches what
   * Shopify thinks). Uses origFetch to skip CartJS's queue entirely.
   */
  function syncCartCountsOnLoad() {
    origFetch('/cart.js?_=' + Date.now(), { headers: { 'Accept': 'application/json' }, cache: 'no-store' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) { if (cart) updateCartCounts(cart); })
      .catch(function () { /* silent — keep Liquid value */ });
  }

  /**
   * Fetch /cart.js with timeout + transparent retry + visible error state.
   *
   * The OLD flow:
   *   - origFetch('/cart.js?_=...') → renderDrawer
   *   - On error: do nothing (left "Loading…" up forever)
   * Symptom: localhost dev server hiccups, drawer stuck on Loading… infinitely.
   * No way for the user to recover without page reload.
   *
   * NEW flow:
   *   1. 5-second timeout via AbortController. Slow networks AND cold dev
   *      proxies both get an upper bound on user pain.
   *   2. One silent retry on first failure — covers transient blips
   *      (Shopify rate limit, dev proxy reconnect, brief net hiccup).
   *   3. If retry also fails → render an explicit error state with a
   *      "Try again" button + "Open full cart page" escape hatch, so the
   *      user always has a way out.
   *
   * The timeout uses `AbortController` → guaranteed to fire even if the
   * underlying network stack hangs forever. Without this, fetch() can
   * sit in pending forever (some dev proxies + SW intercepts cause this).
   */
  var CART_FETCH_TIMEOUT_MS = 5000;
  function fetchCartWithTimeout() {
    var url = '/cart.js?_=' + Date.now();
    var ctrl = new AbortController();
    var timer = setTimeout(function () { ctrl.abort(); }, CART_FETCH_TIMEOUT_MS);
    return origFetch(url, {
      headers: { 'Accept': 'application/json' },
      cache: 'no-store',
      signal: ctrl.signal,
    }).then(function (r) {
      clearTimeout(timer);
      if (!r.ok) throw new Error('cart.js status ' + r.status);
      return r.json();
    }).catch(function (err) {
      clearTimeout(timer);
      throw err;
    });
  }

  function renderDrawerError(retryFn) {
    if (!drawerBodyEl) return;
    clearChildren(drawerBodyEl);
    var box = document.createElement('div');
    box.className = 'ij-drawer-empty';
    box.style.padding = '32px 20px';
    box.style.textAlign = 'center';

    var msg = document.createElement('p');
    msg.textContent = 'Couldn’t load your cart.';
    msg.style.cssText = 'margin: 0 0 8px; color: var(--ij-ink, #1a1a1a); font-family: var(--font-display, Georgia, serif); font-style: italic; font-size: 18px;';
    box.appendChild(msg);

    var sub = document.createElement('p');
    sub.textContent = 'Network or server issue. Try again, or open the full cart page.';
    sub.style.cssText = 'margin: 0 0 20px; color: var(--ij-ink-3, #707070); font-size: 13px;';
    box.appendChild(sub);

    var retry = document.createElement('button');
    retry.type = 'button';
    retry.textContent = 'Try again';
    retry.style.cssText = 'background: var(--ij-ink, #1a1a1a); color: #fff; border: 0; padding: 10px 22px; font: inherit; font-size: 12px; letter-spacing: 0.18em; text-transform: uppercase; cursor: pointer; border-radius: 999px; margin-right: 8px;';
    retry.addEventListener('click', function () {
      renderLoading();
      retryFn();
    });
    box.appendChild(retry);

    var openCart = document.createElement('a');
    openCart.href = '/cart';
    openCart.textContent = 'Open cart page';
    openCart.style.cssText = 'display: inline-block; padding: 10px 18px; font-size: 12px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--ij-ink, #1a1a1a); text-decoration: underline; text-underline-offset: 4px;';
    box.appendChild(openCart);

    drawerBodyEl.appendChild(box);
    if (drawerFootEl) drawerFootEl.style.display = 'none';
  }

  function refreshDrawer() {
    mountDrawer();
    if (!drawerEl) return Promise.resolve();
    /* `cache: 'no-store'` + a cache-bust query param protects against the
       browser serving a stale /cart.js response from right before an
       add/change completed — the symptom is a drawer that opens stuck
       on "Loading…" while the server actually has the new items.
       The fetchCartWithTimeout helper adds a 5s timeout + transparent
       retry so transient failures don't strand the user. */
    return fetchCartWithTimeout()
      .then(renderDrawer)
      .catch(function (err) {
        console.warn('[ij] drawer refresh attempt 1 failed, retrying once', err);
        /* SILENT RETRY (after 600ms) — covers transient blips. */
        return new Promise(function (resolve) { setTimeout(resolve, 600); })
          .then(fetchCartWithTimeout)
          .then(renderDrawer)
          .catch(function (err2) {
            console.error('[ij] drawer refresh failed after retry', err2);
            /* VISIBLE ERROR STATE — user always has a way out. */
            renderDrawerError(refreshDrawer);
          });
      });
  }

  // ========================================================================
  // Header cart icon — open drawer instead of navigating
  // ========================================================================
  function interceptHeaderCart() {
    var link = document.querySelector('.nav-cart a[href*="/cart"]');
    if (!link || link.__ijHooked) return;
    link.__ijHooked = true;
    link.addEventListener('click', function (e) {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
      if (!$('.ij-cart-drawer')) return;
      e.preventDefault();
      openDrawer();
    });
  }

  window.addEventListener('ij:cart-added', function () {
    // /cart/add.js is already complete by the time our wrapper fires this event,
    // so Shopify's internal cart state is up to date. Open immediately; the
    // loading placeholder covers the fraction of a second until /cart.js returns.
    openDrawer();
  });

  window.addEventListener('ij:cart-mutated', function () {
    /* Drawer refresh handles the icon update via renderDrawer →
       updateCartCounts. If drawer is closed, we still need to sync
       the icon to truth — fetch /cart.js silently in that case. */
    if (drawerEl && drawerEl.classList.contains('is-open')) {
      refreshDrawer();
    } else {
      origFetch('/cart.js?_=' + Date.now(), { headers: { 'Accept': 'application/json' }, cache: 'no-store' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) { if (cart) updateCartCounts(cart); })
        .catch(function () { /* silent */ });
    }
  });

  // Charm builder (and any other script) can request the drawer opens by
  // firing `ij:cart:added` on window. Fires after the React bundle's
  // successful /cart/add.js POST — gives the customer an immediate "your
  // piece is in the cart" affordance without them hunting for the cart icon.
  window.addEventListener('ij:cart:added', function () {
    openDrawer();
    /* Use the design-aware count (bundles + loners). Don't write
       cart.item_count directly — that would show "6" for a single
       designed piece, which is what triggered the user complaint. */
    origFetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
      updateCartCounts(cart);
    }).catch(function () { /* ignore */ });
  });

  function init() {
    mountDrawer();
    interceptHeaderCart();
    wireCartButtons();
    /* Initial sync: the Liquid-rendered cart icon shows raw line-item
       count (e.g. "6" for one bundle of a chain + 5 add-ons). Fetch
       once on load and rewrite to design count so the customer never
       sees the misleading raw number. */
    syncCartCountsOnLoad();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.IJ = {
    __ready: true,
    openDrawer: openDrawer,
    closeDrawer: closeDrawer,
    refreshDrawer: refreshDrawer,
    progress: { start: progressStart, stop: progressStop },
  };
})();
